<script setup lang="ts">
import { isVNode, watch, ref } from 'vue'
import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from '.'
import { useToast } from './use-toast'
import { TriangleAlertIcon, CircleCheckIcon, CircleAlertIcon } from 'lucide-vue-next'

const props = defineProps<{
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
  timeout?: number
}>()

const { toasts, dismiss } = useToast()
const hoveredToasts = ref<Set<string>>(new Set())

// Track which toasts are being hovered
function handleMouseEnter(id: string) {
  hoveredToasts.value.add(id)
}

function handleMouseLeave(id: string) {
  hoveredToasts.value.delete(id)
}

// Set up auto-dismiss timers for toasts
watch(
  () => toasts.value,
  newToasts => {
    if (!props.timeout || props.timeout === Infinity) return

    newToasts.forEach(t => {
      if (t.timeout) return

      if (t.open && !t.autoClose) {
        // Mark as auto-closing to prevent multiple timers
        t.autoClose = true
        // Set timeout to auto-dismiss, but check if toast is being hovered
        const dismissTimer = setInterval(() => {
          // Only dismiss if not being hovered
          if (!hoveredToasts.value.has(t.id)) {
            dismiss(t.id)
            clearInterval(dismissTimer)
          }
        }, props.timeout)
      }
    })
  },
  { deep: true },
)
</script>

<template>
  <ToastProvider>
    <Toast
      v-for="toast in toasts"
      :key="toast.id"
      v-bind="toast"
      class="max-w-[420px] flex items-center justify-between border"
      @mouseenter="handleMouseEnter(toast.id)"
      @mouseleave="handleMouseLeave(toast.id)"
    >
      <div class="flex gap-x-4" :class="toast.title && toast.description ? 'items-start' : 'items-center'">
        <div v-if="toast.icon || toast.variant" class="w-5 h-5 flex-none">
          <component :is="toast.icon" v-if="toast.icon" class="text-muted-foreground" />
          <template v-else-if="toast.variant">
            <CircleAlertIcon v-if="toast.variant === 'destructive'" class="text-destructive" :stroke-width="1" />
            <TriangleAlertIcon v-else-if="toast.variant === 'warning'" class="text-warning" :stroke-width="1" />
            <CircleCheckIcon v-else-if="toast.variant === 'success'" class="text-success" :stroke-width="1" />
          </template>
        </div>
        <div class="grid gap-1 pl-2">
          <ToastTitle
            v-if="toast.title"
            class="text-sm font-semibold text-foreground"
            :title="isVNode(toast.title) ? '' : toast.title"
            :aria-label="isVNode(toast.title) ? '' : toast.title"
          >
            <component :is="toast.title" v-if="isVNode(toast.title)" />
            <span v-else>{{ toast.title }}</span>
          </ToastTitle>
          <template v-if="toast.description">
            <ToastDescription v-if="isVNode(toast.description)" class="text-foreground/90">
              <component :is="toast.description" />
            </ToastDescription>
            <ToastDescription v-else class="text-foreground/90 break-words overflow-hidden">
              {{ toast.description }}
            </ToastDescription>
          </template>
        </div>
        <ToastClose class="text-foreground hover:text-muted-foreground" @click="dismiss(toast.id)" />
      </div>
      <component :is="toast.action" />
    </Toast>
    <ToastViewport :position="props.position" />
  </ToastProvider>
</template>
